import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

import {
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  $getSelection,
  $isRangeSelection,
  $createTextNode,
  $insertNodes,
  $getRoot,
  $createParagraphNode,
  $setSelection,
  $createLineBreakNode,
  $createRangeSelection,
} from "lexical";

import { $patchStyleText } from "@lexical/selection";
import { $generateHtmlFromNodes } from "@lexical/html";

// (facultatif mais utile pour listes/quotes)
import { ListNode, ListItemNode } from "@lexical/list";
import { QuoteNode, HeadingNode } from "@lexical/rich-text";

import styles from "./RichEditor.module.css";

/* =========================================
   Load / replace initial HTML (custom import)
   -> préserve color / font-size / formats
   ========================================= */
function LoadHtmlPlugin({ html, syncToken }) {
  const [editor] = useLexicalComposerContext();

  // Conserver la dernière valeur sans re-render
  const htmlRef = useRef(html ?? "");
  useEffect(() => {
    htmlRef.current = html ?? "";
  }, [html]);

  useEffect(() => {
    const currentHtml = htmlRef.current ?? "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(currentHtml, "text/html");

    editor.update(() => {
      const root = $getRoot();
      root.clear();

      const blocks = domToLexicalBlocks(doc.body);
      if (blocks.length === 0) {
        root.append($createParagraphNode());
      } else {
        for (const block of blocks) {
          root.append(block);
        }
      }
    });
  }, [editor, syncToken]);

  return null;
}

/* ===== Helpers d’import minimal qui préservent color / font-size ===== */

function domToLexicalBlocks(containerEl) {
  const out = [];
  const hasBlock = Array.from(containerEl.childNodes).some(
    (n) =>
      n.nodeType === 1 && /^(P|UL|OL|DIV|BLOCKQUOTE|H[1-6])$/.test(n.nodeName)
  );

  if (hasBlock) {
    for (const child of containerEl.childNodes) {
      if (child.nodeType === 1) {
        const tag = child.nodeName;
        if (tag === "P" || tag === "DIV") {
          out.push(buildParagraphFromEl(child));
        } else if (tag === "BR") {
          const p = $createParagraphNode();
          p.append($createLineBreakNode());
          out.push(p);
        } else if (/^H[1-6]$/.test(tag)) {
          out.push(buildParagraphFromEl(child));
        } else if (tag === "UL" || tag === "OL") {
          const lis = child.querySelectorAll(":scope > li");
          lis.forEach((li) => out.push(buildParagraphFromEl(li)));
        } else if (tag === "BLOCKQUOTE") {
          out.push(buildParagraphFromEl(child));
        } else {
          out.push(buildParagraphFromEl(child));
        }
      } else if (child.nodeType === 3) {
        const p = $createParagraphNode();
        p.append(...buildInlineFromNode(child));
        out.push(p);
      }
    }
  } else {
    const p = $createParagraphNode();
    for (const child of containerEl.childNodes) {
      p.append(...buildInlineFromNode(child));
    }
    out.push(p);
  }

  return out;
}

function buildParagraphFromEl(el) {
  const p = $createParagraphNode();
  for (const child of el.childNodes) {
    p.append(...buildInlineFromNode(child));
  }
  return p;
}

/**
 * Construit un fragment Lexical (array de nodes)
 * en préservant:
 *  - <span style="color:…; font-size:…">
 *  - <strong>/<b>, <em>/<i>, <u>, <s>
 *  - <br>
 */
function buildInlineFromNode(node, inheritedStyle = {}) {
  if (node.nodeType === 3) {
    const text = node.nodeValue || "";
    if (!text) return [];
    const t = $createTextNode(text);

    const styleStr = styleObjToCss(inheritedStyle);
    if (styleStr) t.setStyle(styleStr);

    if (inheritedStyle.__bold) t.toggleFormat("bold");
    if (inheritedStyle.__italic) t.toggleFormat("italic");
    if (inheritedStyle.__underline) t.toggleFormat("underline");
    if (inheritedStyle.__strike) t.toggleFormat("strikethrough");

    return [t];
  }

  if (node.nodeType === 1) {
    const tag = node.nodeName;

    if (tag === "BR") {
      return [$createLineBreakNode()];
    }

    const nextStyle = { ...inheritedStyle };

    if (tag === "STRONG" || tag === "B") nextStyle.__bold = true;
    if (tag === "EM" || tag === "I") nextStyle.__italic = true;
    if (tag === "U") nextStyle.__underline = true;
    if (tag === "S") nextStyle.__strike = true;

    if (node.hasAttribute("style")) {
      const parsed = parseColorAndFontSize(node.getAttribute("style") || "");
      if (parsed.color) nextStyle.color = parsed.color;
      if (parsed.fontSize) nextStyle.fontSize = parsed.fontSize;
    }

    const fragment = [];
    for (const child of node.childNodes) {
      fragment.push(...buildInlineFromNode(child, nextStyle));
    }
    return fragment;
  }

  return [];
}

function parseColorAndFontSize(style) {
  const out = {};
  const color = style.match(/(?:^|;)\s*color\s*:\s*([^;]+)\s*(?:;|$)/i);
  const size = style.match(/(?:^|;)\s*font-size\s*:\s*([^;]+)\s*(?:;|$)/i);
  if (color) out.color = color[1].trim();
  if (size) out.fontSize = size[1].trim();
  return out;
}

function styleObjToCss(obj) {
  const parts = [];
  if (obj.color) parts.push(`color: ${obj.color}`);
  if (obj.fontSize) parts.push(`font-size: ${obj.fontSize}`);
  return parts.join("; ");
}

/* ==========================
   Toggle read-only mode
   ========================== */
function EditablePlugin({ readOnly }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);
  return null;
}

/* ==========================
   Toolbar (inside Composer)
   ========================== */
function Toolbar({
  showBold = true,
  showItalic = true,
  showUnderline = true,
  showStrikethrough = true,
  showFontSize = true,
  showTextColor = true,
  showEmoji = true,
  emojis = ["😀", "😂", "😍", "😎", "😢", "👍", "🔥", "🎉", "❤️", "🐴"],
  labels = {},
}) {
  const [editor] = useLexicalComposerContext();
  const [fontSize, setFontSize] = useState("");
  const [active, setActive] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
  });
  const [showEmojiMenu, setShowEmojiMenu] = useState(false);
  const emojiMenuRef = useRef(null);

  /** Garde une copie *sûre* de la sélection (positions), pas l’objet clone */
  const lastSelRef = useRef(null);

  const refreshActive = React.useCallback(() => {
    editor.getEditorState().read(() => {
      const sel = $getSelection();
      if ($isRangeSelection(sel)) {
        // Mémorise les positions (robuste après perte de focus)
        lastSelRef.current = {
          anchorKey: sel.anchor.key,
          anchorOffset: sel.anchor.offset,
          anchorType: sel.anchor.type,
          focusKey: sel.focus.key,
          focusOffset: sel.focus.offset,
          focusType: sel.focus.type,
          // on mémorise aussi style/format pour restaurer exactement
          style: sel.style || "",
          format: sel.format,
        };

        setActive({
          bold: sel.hasFormat("bold"),
          italic: sel.hasFormat("italic"),
          underline: sel.hasFormat("underline"),
          strikethrough: sel.hasFormat("strikethrough"),
        });

        // (optionnel) refléter la taille courante
        // const m = (sel.style || "").match(/(?:^|;)\s*font-size\s*:\s*([^;]+)/i);
        // setFontSize(m ? m[1].trim() : "");
      }
    });
  }, [editor]);

  useEffect(() => {
    const unregisterSelection = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        refreshActive();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
    const unregisterUpdate = editor.registerUpdateListener(() => {
      refreshActive();
    });
    refreshActive();
    return () => {
      unregisterSelection();
      unregisterUpdate();
    };
  }, [editor, refreshActive]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (emojiMenuRef.current && !emojiMenuRef.current.contains(e.target)) {
        setShowEmojiMenu(false);
      }
    };
    if (showEmojiMenu)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showEmojiMenu]);

  // Bouton générique (B/I/U/S)
  const Btn = ({ fmt, label, children }) => {
    const handleMouseDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, fmt);
      queueMicrotask(() => {
        refreshActive();
        editor.focus();
      });
    };
    return (
      <button
        type="button"
        onMouseDown={handleMouseDown}
        aria-pressed={active[fmt]}
        className={active[fmt] ? styles.activeBtn : ""}
        title={label}>
        {children}
      </button>
    );
  };

  /** Snapshot *avant* que le focus quitte l’éditeur (color picker, select…) */
  const snapshotSelectionPointer = (e) => {
    // on ne bloque pas le pointer, juste on capture la sélection
    editor.getEditorState().read(() => {
      const sel = $getSelection();
      if ($isRangeSelection(sel)) {
        lastSelRef.current = {
          anchorKey: sel.anchor.key,
          anchorOffset: sel.anchor.offset,
          anchorType: sel.anchor.type,
          focusKey: sel.focus.key,
          focusOffset: sel.focus.offset,
          focusType: sel.focus.type,
          style: sel.style || "",
          format: sel.format,
        };
      }
    });
  };

  /** Reconstruit une RangeSelection valide dans l’update courante */
  const restoreSelectionOrGetCurrent = () => {
    const saved = lastSelRef.current;
    let sel = $getSelection();
    if ($isRangeSelection(sel)) {
      return sel;
    }
    if (!saved) return null;

    const rs = $createRangeSelection();
    rs.anchor.set(saved.anchorKey, saved.anchorOffset, saved.anchorType);
    rs.focus.set(saved.focusKey, saved.focusOffset, saved.focusType);
    // restaure format/style (utile pour la frappe suivante)
    rs.format = saved.format;
    rs.style = saved.style;
    $setSelection(rs);
    return rs;
  };

  /* -----------------------
     FIX: style persistant
     ----------------------- */

  const mergeStyleStr = (base, patch) => {
    const obj = {};
    (base || "")
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((s) => {
        const i = s.indexOf(":");
        if (i > -1) obj[s.slice(0, i).trim()] = s.slice(i + 1).trim();
      });
    Object.entries(patch).forEach(([k, v]) => {
      if (v == null || v === "") delete obj[k];
      else obj[k] = v;
    });
    return Object.entries(obj)
      .map(([k, v]) => `${k}: ${v}`)
      .join("; ");
  };

  const applyStyle = (patch) => {
    editor.update(() => {
      let sel = restoreSelectionOrGetCurrent();
      if (!sel || !$isRangeSelection(sel)) return;

      // 1) applique sur la sélection réelle
      $patchStyleText(sel, patch);

      // 2) pose la "style mark" pour la frappe suivante
      const merged = mergeStyleStr(sel.style || "", patch);
      sel.setStyle(merged);
    });
    editor.focus();
  };

  // Couleur
  const setColor = (e) => {
    const color = e.target.value;
    applyStyle({ color });
  };

  // Taille
  const _setFontSize = (e) => {
    const size = e.target.value;
    if (!size) return;
    applyStyle({ "font-size": size });
    setFontSize(size);
  };

  const insertEmoji = (emoji) => {
    editor.update(() => {
      const node = $createTextNode(emoji);
      $insertNodes([node]);
    });
    setShowEmojiMenu(false);
    editor.focus();
  };

  return (
    <div
      className={styles.toolbar}
      role="toolbar"
      aria-label={labels.toolbar || "Formatting"}>
      {showBold && (
        <Btn fmt="bold" label={labels.bold || "Bold"}>
          <b>B</b>
        </Btn>
      )}
      {showItalic && (
        <Btn fmt="italic" label={labels.italic || "Italic"}>
          <i>I</i>
        </Btn>
      )}
      {showUnderline && (
        <Btn fmt="underline" label={labels.underline || "Underline"}>
          <u>U</u>
        </Btn>
      )}
      {showStrikethrough && (
        <Btn
          fmt="strikethrough"
          label={labels.strikethrough || "Strikethrough"}>
          <s>S</s>
        </Btn>
      )}

      {showFontSize && (
        <select
          aria-label={labels.fontSize || "Font size"}
          value={fontSize}
          onPointerDown={snapshotSelectionPointer}
          onChange={_setFontSize}>
          <option value="">{labels.size || "Taille"}</option>
          <option value="12px">{labels.sizeSmall || "Petit"}</option>
          <option value="16px">{labels.sizeNormal || "Normal"}</option>
          <option value="20px">{labels.sizeLarge || "Grand"}</option>
          <option value="24px">{labels.sizeXLarge || "Très grand"}</option>
        </select>
      )}

      {showTextColor && (
        <input
          type="color"
          aria-label={labels.textColor || "Text color"}
          onPointerDown={snapshotSelectionPointer}
          onChange={setColor}
        />
      )}

      {showEmoji && (
        <>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowEmojiMenu((v) => !v);
            }}
            title={labels.emoji || "Emoji"}>
            😀
          </button>
          {showEmojiMenu && (
            <div ref={emojiMenuRef} className={styles.emojiMenu}>
              {emojis.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className={styles.emojiBtn}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertEmoji(emoji)}>
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ==========================
   Main component
   ========================== */
const RichEditor = forwardRef(
  (
    {
      // control
      controlled = false,
      initialHTML,
      value,
      onChange,

      // UI
      placeholder = "",
      backgroundColor = "white",
      height = 160,
      readOnly = false,
      autoFocus = false,
      className,
      contentClassName,
      contentStyle,

      // toolbar toggles
      showBold = true,
      showItalic = true,
      showUnderline = true,
      showStrikethrough = true,
      showFontSize = true,
      showTextColor = true,
      showEmoji = true,
      emojis,
      labels = {},

      // form
      name,

      // behavior
      debounceMs = 200,
      flushOnBlur = true,
    },
    ref
  ) => {
    const theme = useMemo(
      () => ({
        text: {
          bold: styles.lxTextBold,
          italic: styles.lxTextItalic,
          underline: styles.lxTextUnderline,
          strikethrough: styles.lxTextStrikethrough,
        },
      }),
      []
    );

    const initialConfig = useMemo(
      () => ({
        namespace: "RichEditor",
        theme,
        onError(error) {
          console.error(error);
        },
        nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode],
      }),
      [theme]
    );

    const lastHtmlRef = useRef((controlled ? value : initialHTML) ?? "");
    const syncTokenRef = useRef(0);
    const isExternallyHydratingRef = useRef(true);

    const debounceTimerRef = useRef(null);

    const rootRef = useRef(null);
    useImperativeHandle(ref, () => ({
      getHTML: () => lastHtmlRef.current || "",
      setHTML: (html) => {
        lastHtmlRef.current = html || "";
        isExternallyHydratingRef.current = true;
        syncTokenRef.current++;
      },
      focus: () => {
        const el = rootRef.current?.querySelector("[contenteditable='true']");
        el?.focus();
      },
      clear: () => {
        lastHtmlRef.current = "";
        isExternallyHydratingRef.current = true;
        syncTokenRef.current++;
      },
    }));

    const emitChange = (html, immediate = false) => {
      lastHtmlRef.current = html;
      if (!onChange) return;

      if (immediate || debounceMs <= 0) {
        onChange(html);
        return;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        onChange(html);
      }, debounceMs);
    };

    const handleChange = (editorState, editor) => {
      if (isExternallyHydratingRef.current) {
        isExternallyHydratingRef.current = false;
        return;
      }
      const html = editorState.read(() => $generateHtmlFromNodes(editor));
      if (html === lastHtmlRef.current) return;
      emitChange(html, false);
    };

    useEffect(() => {
      if (!controlled) return;
      if (value === undefined) return;
      if (value !== lastHtmlRef.current) {
        lastHtmlRef.current = value || "";
        isExternallyHydratingRef.current = true;
        syncTokenRef.current++;
      }
    }, [controlled, value]);

    useEffect(() => {
      if (!autoFocus) return;
      const id = requestAnimationFrame(() => {
        const el = rootRef.current?.querySelector("[contenteditable='true']");
        el?.focus();
      });
      return () => cancelAnimationFrame(id);
    }, [autoFocus]);

    const handleBlur = () => {
      if (!flushOnBlur || !onChange) return;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      onChange(lastHtmlRef.current);
    };

    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    return (
      <div
        className={`${styles.container} ${className || ""}`}
        ref={rootRef}
        style={{ height }}>
        <LexicalComposer initialConfig={initialConfig}>
          <Toolbar
            showBold={showBold}
            showItalic={showItalic}
            showUnderline={showUnderline}
            showStrikethrough={showStrikethrough}
            showFontSize={showFontSize}
            showTextColor={showTextColor}
            showEmoji={showEmoji}
            emojis={emojis}
            labels={labels}
          />

          <EditablePlugin readOnly={readOnly} />

          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={`${styles.editor} ${contentClassName || ""}`}
                style={{ backgroundColor, ...contentStyle }}
                aria-label={labels.ariaLabel || "Rich text editor"}
                onBlur={handleBlur}
              />
            }
            placeholder={
              <div className={styles.placeholder}>
                {labels.placeholder || placeholder}
              </div>
            }
          />

          <HistoryPlugin />

          <LoadHtmlPlugin
            html={lastHtmlRef.current}
            syncToken={syncTokenRef.current}
          />

          <OnChangePlugin onChange={handleChange} />
        </LexicalComposer>

        {name ? (
          <textarea name={name} value={lastHtmlRef.current} readOnly hidden />
        ) : null}
      </div>
    );
  }
);

export default RichEditor;
